import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  Input,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { Dropdown } from 'primeng/dropdown';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  ControlIdentity,
  CplaceSingleSelectControlChange,
  CplaceSingleSelectControlParameters,
  Option,
  OptionGroup,
} from '../../../api/model/models';
import { CplaceControlWithEditModeComponent, CPLACE_CONTROL_COMPONENT_VALUE_ACCESSOR } from '../shared';
import { CplaceControlService } from '../shared/cplace-control.service';

@Component({
  selector: 'cf-frontend-sdk-cplace-single-select-control',
  templateUrl: './cplace-single-select-control.component.html',
  styleUrls: [
    '../cplace-single-text-control/styles/cplace-single-text-control.component.scss',
    './styles/cplace-single-select-control.component.scss',
  ],
  providers: [CPLACE_CONTROL_COMPONENT_VALUE_ACCESSOR(CplaceSingleSelectControlComponent)],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CplaceSingleSelectControlComponent extends CplaceControlWithEditModeComponent<string | undefined> {
  public static readonly componentName = 'CplaceSingleSelectControlComponent';

  /**
   * Reference to the PrimeNG Dropdown component.
   * @private
   */
  @ViewChild(Dropdown, { static: true })
  private dropdown!: Dropdown;

  /**
   * Label of the option shown in the input when an item of type {@link OptionGroup} is selected.
   */
  public selectedGroupOptionLabel?: string;

  /**
   * Options shown in the dropdown to choose from.
   */
  @Input() public set options(val: Option[] | undefined) {
    this._options = val;
    this.setDisplayOptions();
    this.setSelectedGroupOptionLabel();
  }
  public get options(): Option[] | undefined {
    return this._options;
  }
  private _options?: Option[];

  /**
   * Option groups shown in the dropdown to choose from.
   */
  @Input() public set optionGroups(val: OptionGroup[] | undefined) {
    this._optionGroups = val;
    this.setDisplayOptions();
    this.setSelectedGroupOptionLabel();
  }
  public get optionGroups(): OptionGroup[] | undefined {
    return this._optionGroups;
  }
  private _optionGroups?: OptionGroup[];

  /**
   * Whether the clearable icon should be shown.
   */
  @Input() public clearable = true;

  /**
   * Actual array of options that gets passed to the PrimeNG component.
   */
  public displayOptions: Option[] | OptionGroup[] = [];

  @HostBinding('class.cf-frontend-sdk-cplace-single-select-control') public cplaceSingleSelectControl = true;

  /**
   * Whether the empty value view should be shown.
   */
  public $showEmptyValue: Observable<boolean> = combineLatest([this.$value, this.$editMode]).pipe(
    map(([value, editMode]) => {
      return !value && !editMode;
    })
  );

  /**
   * Whether the clear button should be shown.
   */
  public $showClear: Observable<boolean> = combineLatest([this.$value, this.$editMode]).pipe(
    map(([value, editMode]) => {
      return !!value && editMode;
    })
  );

  constructor(
    public elementRef: ElementRef,
    public changeDetectorRef: ChangeDetectorRef,
    public cplaceControlService: CplaceControlService
  ) {
    super(elementRef, changeDetectorRef, cplaceControlService);
  }

  /**
   * Handles the ngModelChange event.
   */
  public onNgModelChange() {
    this.setSelectedGroupOptionLabel();
    this.dropdown.focus();
  }

  /**
   * Handles the on cancel event.
   */
  public onCancel(): void {
    super.onCancel();
    this.setSelectedGroupOptionLabel();
  }

  /**
   * Sets the options that are passed to PrimeNG dropdown.
   */
  private setDisplayOptions(): void {
    this.displayOptions = this.optionGroups ? this.optionGroups : this.options ? this.options : [];
  }

  /**
   * Sets the label displayed in the input field of the PrimeNG dropdown.
   */
  private setSelectedGroupOptionLabel(): void {
    if (this.optionGroups) {
      const groupLabel = this.optionGroups?.find((items) => items.options?.find((item) => item.value === this.value))?.label;
      this.selectedGroupOptionLabel = `${groupLabel} > ${this.value}`;
    }
  }

  protected initializeControlParameters(controlParameters: CplaceSingleSelectControlParameters): void {
    this.value = controlParameters.value;
    this.clearable = controlParameters.clearable;
    this.options = controlParameters.options;
    this.optionGroups = controlParameters.optionGroups;
  }

  protected getControlChange(identity: ControlIdentity, value: string | undefined): CplaceSingleSelectControlChange {
    return { featureName: identity.featureName, componentName: CplaceSingleSelectControlComponent.componentName, value: value };
  }

  protected getComponentClassName(): string {
    return CplaceSingleSelectControlComponent.componentName;
  }

  protected onClear(): void {
    this.value = undefined;
  }

  protected afterSubmitHook() {
    super.afterSubmitHook();
    this.dropdown.focused = false;
  }
}
